import {
  ActionComponent,
  Condition,
  ConditionExpression,
  EBIcon,
  IconRefMap,
  Namespace,
  Ref,
} from '@repo/sdk/models/components';

/**
 * Type guard to check if a value is a Ref object
 * @param value - The value to check
 * @returns True if the value has the structure of a Ref object
 */
export const isRef = (value: unknown): value is Ref => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof (value as any).id === 'string' &&
    'namespace' in value &&
    'valueKind' in value
  );
};

const refDedupeKey = (ref: Ref) => `${ref.id}:${ref.namespace}`;

/** Drops duplicate refs (same id + namespace), keeping first occurrence order. */
const dedupeRefs = (refs: Ref[]): Ref[] => {
  const seen = new Set<string>();
  const out: Ref[] = [];
  for (const ref of refs) {
    const key = refDedupeKey(ref);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ref);
  }
  return out;
};

/**
 * Extracts refs from a condition expression based on namespace filtering
 * Handles all three condition expression types: condition, conditionGroup, and nestedExpression
 * @param conditionExpression - The condition expression to extract refs from
 * @param isSetting - If true, extract only Setting refs; if false, extract non-Setting refs
 * @returns Array of Ref objects from the condition expression
 */
export const extractRefsFromConditionExpression = (
  conditionExpression: ConditionExpression | undefined,
  isSetting: boolean
): Ref[] => {
  if (!conditionExpression) return [];

  const refs: Ref[] = [];
  const addRefIfNamespaceMatches = (ref: Ref | undefined | null) => {
    if (
      ref &&
      (isSetting ? ref.namespace === Namespace.Setting : ref.namespace !== Namespace.Setting)
    ) {
      refs.push(ref);
    }
  };

  const addConditionRefs = (condition: Condition) => {
    addRefIfNamespaceMatches(condition.ref);
    addRefIfNamespaceMatches(condition.secondaryRef);
  };

  // Case 1: Single condition with a ref
  if (conditionExpression.condition) {
    addConditionRefs(conditionExpression.condition);
  }
  // Case 2: Condition group with multiple conditions
  if (conditionExpression.conditionGroup) {
    conditionExpression.conditionGroup.conditions?.forEach((condition: Condition) => {
      addConditionRefs(condition);
    });
  }
  // Case 3: Nested expression with groups of conditions
  if (conditionExpression.nestedExpression) {
    conditionExpression.nestedExpression.groups?.forEach(
      ({ conditions }: { conditions: Condition[] }) => {
        conditions?.forEach((condition: Condition) => {
          addConditionRefs(condition);
        });
      }
    );
  }

  return dedupeRefs(refs);
};

/**
 * Extracts refs from action components
 * @param actionComponents - Array of action components
 * @param isSetting - If true, extract only Setting refs; if false, extract non-Setting refs
 * @returns Array of Ref objects
 */
export const extractActionComponentRefs = (
  actionComponents: ActionComponent[],
  isSetting: boolean
): Ref[] => {
  const refs: Ref[] = [];

  actionComponents?.forEach(({ states }) => {
    states?.forEach(
      ({ conditionExpression }: { conditionExpression?: ConditionExpression | undefined }) => {
        // Extract refs from condition expression using helper
        refs.push(...extractRefsFromConditionExpression(conditionExpression, isSetting));
      }
    );
  });

  return refs;
};

/**
 * Extracts refs from an icon
 *
 * @param icon - The icon to extract refs from
 * @returns Object with readingRefs and settingRefs arrays
 */
export const extractIconRefs = (
  icon: EBIcon | undefined
): { readingRefs: Ref[]; settingRefs: Ref[] } => {
  if (!icon) {
    return { readingRefs: [], settingRefs: [] };
  }

  const readingRefs: Ref[] = [];
  const settingRefs: Ref[] = [];

  icon.states?.forEach(
    ({
      conditionExpression,
      iconRefMap,
    }: {
      conditionExpression?: ConditionExpression | undefined;
      iconRefMap?: IconRefMap | undefined;
    }) => {
      // Extract refs from condition expression
      readingRefs.push(...extractRefsFromConditionExpression(conditionExpression, false));
      settingRefs.push(...extractRefsFromConditionExpression(conditionExpression, true));

      // Extract ref from iconRefMap if present
      if (iconRefMap?.ref) {
        if (iconRefMap.ref.namespace === Namespace.Setting) {
          settingRefs.push(iconRefMap.ref);
        } else {
          readingRefs.push(iconRefMap.ref);
        }
      }
    }
  );

  return { readingRefs, settingRefs };
};
